import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { BestOfWoltCalculationResult } from 'apis/merchant-gateway/best-of-wolt/types';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';

import BestOfWoltStar from '../images/BestOfWoltStar.png';
import PleadingFaceEmoji from '../images/PleadingFace.png';

type StatusBannerProps = {
  relativeDate: DateTime;
  calculationResult: BestOfWoltCalculationResult;
  startDate: string;
  endDate: string;
  timezone: string;
};

const StatusBannerContainer = styled.div<{ isBestOfWolt: boolean | null }>`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid;
  min-height: 116px;
  border-color: ${({ isBestOfWolt }) =>
    isBestOfWolt === null ? cssThemedValue('borderPrimary') : isBestOfWolt ? colors.banana32 : colors.strawberry32};
  background-color: ${({ isBestOfWolt }) =>
    isBestOfWolt === null
      ? cssThemedValue({
          light: colors.pepper8,
          dark: colors.salt12,
        })
      : isBestOfWolt
        ? colors.banana8
        : colors.strawberry8};

  @media (width < ${MOBILE_BREAKPOINT}px) {
    gap: 16px;
  }
`;

const StatusInformationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  flex: 1;
  gap: 8px;
`;

const StatusInformationText = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const StatusTitle = styled(Typography.Heading5)``;

const BestOfWoltStarSuccess = styled.img.attrs({
  src: BestOfWoltStar,
})`
  fill: ${cssThemedValue('textSecondary')};
  width: 80px;
  height: 80px;

  @media (width < ${MOBILE_BREAKPOINT}px) {
    width: 42px;
    height: 42px;
  }
`;

const BestOfWoltStarProgress = styled(BestOfWoltStarSuccess)`
  filter: grayscale(1);
`;

const PleadingFaceEmojiImg = styled.img`
  width: 64px;
  height: 64px;

  @media (width < ${MOBILE_BREAKPOINT}px) {
    width: 42px;
    height: 42px;
  }
`;

export const StatusBanner = ({ relativeDate, calculationResult, startDate, endDate, timezone }: StatusBannerProps) => {
  const { locale } = useIntl();
  const now = DateTime.now().setZone(timezone);
  const startMonthAndDate = DateTime.fromISO(startDate)
    .setZone(timezone)
    .setLocale(locale)
    .toLocaleString({ month: 'long', day: 'numeric' });
  const endDateLuxon = DateTime.fromISO(endDate).setZone(timezone).minus({ month: 1 }).endOf('month');
  const endMonthAndDate = endDateLuxon.setLocale(locale).toLocaleString({ month: 'long', day: 'numeric' });
  const daysRemaining = endDateLuxon.diff(now, ['day', 'hour']).days; // add hour for rounding up
  const relativeMonth = relativeDate.setLocale(locale).toLocaleString({ month: 'long' });
  const isBestOfWolt =
    calculationResult.status === 'DONE' && calculationResult.details ? calculationResult.details.isBoW : null;
  return (
    <StatusBannerContainer isBestOfWolt={isBestOfWolt}>
      {calculationResult.status === 'DONE' && (
        <>
          {/* Calculation is done. Show whether you are best of wolt */}
          {isBestOfWolt === true && (
            <>
              <BestOfWoltStarSuccess />
              <StatusInformationContainer>
                <StatusInformationText>
                  <StatusTitle>
                    <FormattedMessage
                      id="best-of-wolt.status.success.title"
                      values={{
                        relativeMonth,
                      }}
                    />
                  </StatusTitle>
                  <Typography.Body2>
                    <FormattedMessage
                      id="best-of-wolt.status.tracking-period"
                      values={{
                        startMonthAndDate,
                        endMonthAndDate,
                      }}
                    />
                  </Typography.Body2>
                </StatusInformationText>
                <Typography.Body2>
                  <FormattedMessage
                    id="best-of-wolt.status.success.description"
                    values={{
                      relativeMonth,
                    }}
                  />
                </Typography.Body2>
              </StatusInformationContainer>
            </>
          )}
          {isBestOfWolt === false && (
            <>
              <PleadingFaceEmojiImg src={PleadingFaceEmoji} />
              <StatusInformationContainer>
                <StatusInformationText>
                  <StatusTitle>
                    <FormattedMessage
                      id="best-of-wolt.status.failed.title"
                      values={{
                        relativeMonth,
                      }}
                    />
                  </StatusTitle>
                  <Typography.Body2>
                    <FormattedMessage
                      id="best-of-wolt.status.tracking-period"
                      values={{
                        startMonthAndDate,
                        endMonthAndDate,
                      }}
                    />
                  </Typography.Body2>
                </StatusInformationText>
                <Typography.Body2>
                  <FormattedMessage
                    id="best-of-wolt.status.failed.description"
                    values={{
                      relativeMonth,
                    }}
                  />
                </Typography.Body2>
              </StatusInformationContainer>
            </>
          )}
        </>
      )}

      {calculationResult.status === 'CALCULATING' && (
        <>
          <BestOfWoltStarProgress />
          <StatusInformationText>
            <StatusTitle>
              <FormattedMessage
                id="best-of-wolt.status.calculating.title"
                values={{
                  relativeMonth,
                }}
              />
            </StatusTitle>
            <Typography.Body2>
              <FormattedMessage
                id="best-of-wolt.status.calculating.description"
                values={{
                  bold: (text: string) => <b>{text}</b>,
                  relativeMonth,
                }}
              />
            </Typography.Body2>
          </StatusInformationText>
        </>
      )}

      {calculationResult.status === 'IN_PROGRESS' && (
        <>
          <BestOfWoltStarProgress />
          <StatusInformationText>
            <StatusTitle>
              <FormattedMessage
                id="best-of-wolt.status.default.title"
                values={{
                  relativeMonth,
                }}
              />
            </StatusTitle>
            <Typography.Body2>
              <FormattedMessage
                id="best-of-wolt.status.default.description"
                values={{
                  bold: (text: string) => <b>{text}</b>,
                  daysRemaining,
                  startMonthAndDate,
                  endMonthAndDate,
                }}
              />
            </Typography.Body2>
          </StatusInformationText>
        </>
      )}

      {calculationResult.status === 'NOT_STARTED' && (
        <>
          <BestOfWoltStarProgress />
          <StatusInformationText>
            <StatusTitle>
              <FormattedMessage
                id="best-of-wolt.status.not-started.title"
                values={{
                  relativeMonth,
                }}
              />
            </StatusTitle>
            <Typography.Body2>
              <FormattedMessage
                id="best-of-wolt.status.not-started.description"
                values={{
                  bold: (text: string) => <b>{text}</b>,
                  relativeMonth,
                }}
              />
            </Typography.Body2>
          </StatusInformationText>
        </>
      )}
    </StatusBannerContainer>
  );
};
